import React, { forwardRef } from 'react';

import { formatNumber, getFormatDate } from '@/utils/common';

interface Viewer {
  contractInfo?: ContractForm;
}

const Viewer = forwardRef<HTMLDivElement, Viewer>(({ contractInfo }, ref) => {
  return (
    <div ref={ref} className="pt-[2px] pb-[60px] text-center min-w-[248px] bg-white rounded-b-[12px] p-4">
      <h4 className="font-bold text-[28px] text-center pt-[30px]">자동차 관리권 양도 양수 계약서</h4>
      <p className="text-center mb-3 mt-2 text-[20px] font-medium">
        매도인과 매수인은 쌍방 합의하에 매매계약을 다음과 같이 체결한다.
      </p>
      <table className="text-left w-full border border-black border-collapse mb-4 text-[18px] break-words font-medium">
        <colgroup>
          <col className="w-[14%]" />
          <col className="w-[14%]" />
          <col className="w-[14%]" />
          <col className="w-[14%]" />
          <col className="w-[14%]" />
          <col className="w-[14%]" />
          <col className="w-[14%]" />
        </colgroup>
        <tbody>
          <tr className="border border-black">
            <th colSpan={7} className="p-2 font-medium text-left border border-black">
              <div className="flex items-center justify-between">
                <div>1.매매할 자동차의 표시</div>
              </div>
            </th>
          </tr>
          <tr className="border border-black">
            <th className="p-2 font-medium border border-black">차량번호</th>
            <td colSpan={2} className="p-2 border border-black">
              {contractInfo?.carNumber}
            </td>
            <th className="p-2 font-medium border border-black">차종</th>
            <td colSpan={3} className="p-2 border border-black">
              {contractInfo?.carType}
            </td>
          </tr>
          <tr className="border border-black">
            <th className="p-2 font-medium border border-black">용도</th>
            <td colSpan={2} className="p-2 border border-black">
              {contractInfo?.carUse}
            </td>
            <th className="p-2 font-medium border border-black">차명</th>
            <td colSpan={3} className="p-2 border border-black">
              {contractInfo?.carName}
            </td>
          </tr>
          <tr className="border border-black">
            <th className="p-2 font-medium border border-black">연식</th>
            <td colSpan={2} className="border border-black p-2 max-w-[280px]">
              {getFormatDate(contractInfo?.year, ['year', 'month'])}
            </td>
            <th className="p-2 font-medium border border-black">원동기 형식</th>
            <td colSpan={3} className="p-2 border border-black">
              {contractInfo?.motorType}
            </td>
          </tr>
          <tr className="border border-black">
            <th className="p-2 font-medium border border-black">차대번호</th>
            <td colSpan={6} className="p-2 border border-black">
              {contractInfo?.identificationNumber}
            </td>
          </tr>
          <tr className="border border-black">
            <th colSpan={7} className="p-2 font-medium text-left border border-black">
              2.계약내용(약정사항)
            </th>
          </tr>
          <tr className="border border-black">
            <th colSpan={7} className="p-2 font-medium text-left border border-black">
              재 1조 위 자동차를 매매함에 있어 매매 금액을 아래와 같이 지불하기로 한다.
            </th>
          </tr>
          <tr className="border border-black">
            <th className="p-2 font-medium border border-black">매매금액</th>
            <td colSpan={6} className="p-2 border border-black">
              一金 원정은 {`${formatNumber(String(contractInfo?.tradingAmount))}원`}정
              {` (₩${contractInfo?.tradingAmount && formatNumber(String(contractInfo?.tradingAmount))})`}
            </td>
          </tr>
          <tr className="border border-black">
            <th className="p-2 font-medium">계약금</th>
            <td colSpan={6} className="p-2 border border-black">
              一金 <>{contractInfo?.downPayment ? `${formatNumber(String(contractInfo?.downPayment))}원` : ''}</>
              정은 {contractInfo?.downPaymentDate ? getFormatDate(String(contractInfo?.downPaymentDate)) : ''} 계약시
              지불하고
            </td>
          </tr>
          <tr className="border border-black">
            <th className="p-2 font-medium">중도금</th>
            <td colSpan={6} className="p-2 border border-black">
              一金{' '}
              <>
                {contractInfo?.intermediatePayment
                  ? `${formatNumber(String(contractInfo?.intermediatePayment))}원`
                  : ''}
              </>
              정은{' '}
              {contractInfo?.intermediatePaymentDate
                ? getFormatDate(String(contractInfo?.intermediatePaymentDate))
                : ''}{' '}
              계약시 지불한다
            </td>
          </tr>
          <tr className="border border-black">
            <th className="p-2 font-medium">잔금</th>
            <td colSpan={6} className="p-2 border border-black">
              一金 <>{contractInfo?.balance ? `${formatNumber(String(contractInfo?.balance))}원` : ''}</>
              정은 {contractInfo?.balancePaymentDate
                ? getFormatDate(String(contractInfo?.balancePaymentDate))
                : ''}{' '}
              계약시 지불한다
            </td>
          </tr>
          <tr className="border border-black">
            <td colSpan={7} className="p-2 leading-6 border border-black">
              <div className="flex mt-2 mb-3.5">
                <p className="w-14">제2조</p>
                <p className="flex-1">(당사자 표시) 매도인을 “갑”이라 하고 매수인을 “을”이라 한다.</p>
              </div>
              <div className="flex mb-3.5">
                <p className="w-14">제3조</p>
                <p className="flex-1">
                  (동시이행등) “갑”은 잔금수령과 상환으로 자동차와 소유권 이전등록에 필요한 서류를 “을”에게 인도한다.
                  영업용 차량일 경우 매수인은 지입회사의 규정에 준하여 위수탁관리 계약을 체결한다.
                </p>
              </div>
              <div className="flex mb-3.5">
                <p className="w-14">제4조</p>
                <p className="flex-1">
                  (공과금부담) 이 자동차에 대한 제세공과금은 자동차 인도일을 기준으로 하여 그 기준일까지의 분은 “갑”이
                  부담하고 기준일의 다음날부터의 분은 “을”이 부담한다. 영업용 경우 사후 발생하는 차량에 대한 모든 보험료
                  및 제세공과금 지입료부분을 운수회사 규정에 준한다.
                </p>
              </div>
              <div className="flex mb-3.5">
                <p className="w-14">제5조</p>
                <p className="flex-1">
                  (하자담보책임) “을”은 이 자동차를 인수한 후에는 이 자동차의 고장 또는 불량 등의 사유로 “갑”에게 그
                  책임을 물을 수 없다.
                </p>
              </div>
              <div className="flex mb-3.5">
                <p className="w-14">제6조</p>
                <p className="flex-1">
                  (사고책임) “을”은 이 자동차를 인수한 때부터 발생하는 모든 사고에 대하여 자기를 위하여 운행하는
                  자로서의 책임을 진다.
                </p>
              </div>
              <div className="flex mb-3.5">
                <p className="w-14">제7조</p>
                <p className="flex-1">
                  (법률상의 하자책임) 자동차인도일 이전에 발생한 행정처분 또는 이전 등록요건의 불비 기타행정상의 하자에
                  대하여는 “갑”이 그 책임을 진다.
                </p>
              </div>
              <div className="flex mb-3.5">
                <p className="w-14">제8조</p>
                <p className="flex-1">
                  (등록지체책임) “을”이 이 매매 목적물을 인수한 후 소정의 기일안에 이전등록을 하지 아니할 때에는 이에
                  대한 모든 책임을 “을”이 진다.
                </p>
              </div>
              <div className="flex mb-3.5">
                <p className="w-14">제9조</p>
                <p className="flex-1">
                  (할부승계특약) “갑”이 자동차를 할부로 구입하여 할부금을 완납하지 않은 상태에서 “을”에게 양도하는
                  경우에는 잔여할부금을 “을”이 승계하여 부담할 것인지의 여부를 특약사항란에 기재하여야 한다.
                </p>
              </div>
              <div className="flex mb-3.5">
                <p className="w-14">제10조</p>
                <p className="flex-1">
                  (위약금) 매도인이 위약시는 위약금조로 계약금의 배역을 배상하기로 하고 매수인이 위약시는 위약금조로
                  계약금을 포기하기로 한다.
                </p>
              </div>
              <div className="flex mb-2">
                <p className="w-14">제11조</p>
                <p className="flex-1">(잔금불이행책임) 잔금 약속 불이행시 당 차량을 포기한다.</p>
              </div>
            </td>
          </tr>
          <tr style={{ border: '1px solid black' }}>
            <td colSpan={7} style={{ lineHeight: '24px', border: '1px solid black', padding: '8px' }}>
              <span style={{ display: 'block', paddingBottom: '8px' }}>특약사항</span>
              매도인, 매수인 쌍방의 매매계약서로 충분히 유효하며, [자동차등록규칙] 제33조제2항제1호에 따라 이
              양도증명서를 작성함.
              <br />
              {contractInfo?.additionalConditions.split('\n').map((line, index) => {
                return (
                  <span key={index}>
                    {line}
                    <br />
                  </span>
                );
              })}
            </td>
          </tr>
          <tr className="border border-black">
            <th rowSpan={4} className="font-medium text-center align-top border border-black">
              (갑)
              <br />
              매도인
            </th>
            <td colSpan={2} className="p-2 border border-black">
              주소
            </td>
            <td colSpan={4} className="p-2 border border-black">
              {contractInfo?.transferorAddress}
            </td>
          </tr>
          <tr className="border border-black">
            <td colSpan={2} className="p-2 border border-black">
              성명(인)
            </td>
            <td colSpan={4} className="p-2 border border-black">
              <div className="flex items-center">
                {contractInfo?.transferorName}
                <div className="px-4 w-[100px]">
                  <img
                    src={contractInfo?.transferorSignImageUrl}
                    alt="매도인 서명"
                    className="max-w-[100px] max-h-[20px]"
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
            </td>
          </tr>
          <tr className="border border-black">
            <td colSpan={2} className="p-2 border border-black">
              전화번호
            </td>
            <td colSpan={4} className="p-2 border border-black">
              {contractInfo?.transferorPhoneNumber}
            </td>
          </tr>
          <tr className="border border-black">
            <td colSpan={2} className="p-2 border border-black">
              주민등록번호(사업자번호)
            </td>
            <td colSpan={4} className="p-2 border border-black">
              {contractInfo?.transferorRegistrationNumber}
            </td>
          </tr>

          <tr className="border border-black">
            <th rowSpan={4} className="font-medium text-center align-top border border-black">
              (을)
              <br />
              매수인
            </th>
            <td colSpan={2} className="p-2 border border-black">
              주소
            </td>
            <td colSpan={4} className="p-2 border border-black">
              {contractInfo?.transfereeAddress}
            </td>
          </tr>

          <tr className="border border-black">
            <td colSpan={2} className="p-2 border border-black">
              성명
            </td>
            <td colSpan={4} className="p-2 border border-black">
              <div className="flex items-center">
                {contractInfo?.transfereeName}
                <div className="px-4 w-[100px]">
                  <img
                    src={contractInfo?.transfereeSignImageUrl}
                    alt="매수인 서명"
                    className="max-w-[100px] max-h-[20px]"
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
            </td>
          </tr>

          <tr className="border border-black">
            <td colSpan={2} className="p-2 border border-black">
              전화번호
            </td>
            <td colSpan={4} className="p-2 border border-black">
              {contractInfo?.transfereePhoneNumber}
            </td>
          </tr>

          <tr className="border border-black">
            <td colSpan={2} className="p-2 border border-black">
              주민등록번호(사업자번호)
            </td>
            <td colSpan={4} className="p-2 border border-black">
              {contractInfo?.transfereeRegistrationNumber}
            </td>
          </tr>

          <tr className="border border-black">
            <th rowSpan={2} className="font-medium text-center align-top border border-black">
              입회인
            </th>
            <td colSpan={2} className="p-2 border border-black">
              성명
            </td>
            <td colSpan={4} className="p-2 border border-black">
              <div className="flex items-center">
                {contractInfo?.representativeName}
                {contractInfo?.representativeSignImageUrl && (
                  <div className="px-4 w-[100px]">
                    <img
                      src={contractInfo?.representativeSignImageUrl}
                      alt="입회인 서명"
                      className="max-w-[100px] max-h-[20px]"
                      crossOrigin="anonymous"
                    />
                  </div>
                )}
              </div>
            </td>
          </tr>
          <tr className="border border-black">
            <td colSpan={2} className="p-2 border border-black">
              전화번호
            </td>
            <td colSpan={4} className="p-2 border border-black">
              {contractInfo?.representativePhoneNumber}
            </td>
          </tr>
        </tbody>
      </table>
      <div className="text-center text-[18px]">{contractInfo?.completedDate}</div>
    </div>
  );
});

export default Viewer;
